import { WSAvatarProps } from "@wingspanhq/fe-component-library";
import type { AuditLog } from "@wingspanhq/wingspan-api-sdk";

/**
 * v3 audit logs identify the principal that caused an entry by `actorId` plus a
 * resolved `actorName`. They no longer classify it: the `actorType` the timeline
 * used to label and icon entries by (`Person` / `ServiceAccount` / `System` /
 * `WingspanStaff` / `LegacyPrincipal`) is not part of the model, so the label is
 * the actor's own name and the icon falls back to the one distinction still
 * derivable — an entry with no principal at all was not caused by a person.
 */
const UNKNOWN_ACTOR_LABEL = "Unknown";

export const formatActorLabel = (event: AuditLog): string =>
  event.actorName || UNKNOWN_ACTOR_LABEL;

/**
 * An entry carrying neither an id nor a name has no principal behind it, which
 * is what the removed `System` / `ServiceAccount` actor types described — so it
 * keeps the automated icon. Everything else is attributed to a person.
 */
export const formatActorAvatar = (event: AuditLog): WSAvatarProps =>
  !event.actorId && !event.actorName
    ? { type: "icon", icon: "bot" }
    : { type: "icon", icon: "person" };

/**
 * Identity of the acting principal, for grouping consecutive entries by actor.
 * Two entries only group when the same principal caused them, so the display
 * label alone is not enough — two different people can share a name. `actorId`
 * is the identity when present, with the name as a fallback for the legacy rows
 * that carry no id.
 */
export const formatActorIdentity = (event: AuditLog): string =>
  event.actorId ?? event.actorName ?? "";
